import { useState } from 'react';
import { useCreateDiagnosis } from '@/hooks';
import { CID10Autocomplete } from './CID10Autocomplete';
import { Button } from '@/components/ui/Button';
import { type DiagnosisType } from '@/api';

interface DiagnosisSelectorProps {
  medicalRecordId: string;
  existingDiagnoses?: Array<{
    id: string;
    cidCode?: string;
    description: string;
    type: DiagnosisType;
  }>;
  onDiagnosisAdded?: () => void;
  disabled?: boolean;
}

const diagnosisTypeOptions: { value: DiagnosisType; label: string }[] = [
  { value: 'PRINCIPAL', label: 'Principal' },
  { value: 'SECONDARY', label: 'Secundário' },
  { value: 'DIFFERENTIAL', label: 'Diferencial' },
];

export function DiagnosisSelector({
  medicalRecordId,
  existingDiagnoses = [],
  onDiagnosisAdded,
  disabled = false,
}: DiagnosisSelectorProps) {
  const [showForm, setShowForm] = useState(false);
  const [selectedCidCode, setSelectedCidCode] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<DiagnosisType>('PRINCIPAL');

  const createDiagnosis = useCreateDiagnosis();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) return;

    try {
      await createDiagnosis.mutateAsync({
        medicalRecordId,
        cidCode: selectedCidCode || undefined,
        description: description.trim(),
        type,
      });
      setShowForm(false);
      setSelectedCidCode('');
      setDescription('');
      setType('PRINCIPAL');
      onDiagnosisAdded?.();
    } catch (error) {
      console.error('Failed to add diagnosis:', error);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setSelectedCidCode('');
    setDescription('');
    setType('PRINCIPAL');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Diagnósticos</h3>
        {!showForm && !disabled && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowForm(true)}
          >
            + Adicionar Diagnóstico
          </Button>
        )}
      </div>

      {existingDiagnoses.length > 0 && (
        <div className="space-y-2">
          {existingDiagnoses.map((diagnosis) => (
            <div
              key={diagnosis.id}
              className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {diagnosis.cidCode && (
                    <span className="px-2 py-0.5 bg-primary-100 text-primary-700 rounded text-xs font-mono">
                      {diagnosis.cidCode}
                    </span>
                  )}
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    diagnosis.type === 'PRINCIPAL'
                      ? 'bg-purple-100 text-purple-700'
                      : diagnosis.type === 'SECONDARY'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-orange-100 text-orange-700'
                  }`}>
                    {diagnosisTypeOptions.find(t => t.value === diagnosis.type)?.label}
                  </span>
                </div>
                <p className="mt-1 text-sm text-gray-700">{diagnosis.description}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Código CID-10 (opcional)
            </label>
            <CID10Autocomplete
              value={selectedCidCode}
              onChange={(code) => {
                setSelectedCidCode(code);
              }}
              onSelect={(_, desc) => {
                if (!description) setDescription(desc);
              }}
              placeholder="Buscar código CID-10..."
              disabled={disabled || createDiagnosis.isPending}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrição do Diagnóstico *
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Descreva o diagnóstico..."
              disabled={disabled || createDiagnosis.isPending}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Diagnóstico *
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as DiagnosisType)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              disabled={disabled || createDiagnosis.isPending}
              required
            >
              {diagnosisTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={createDiagnosis.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!description.trim() || createDiagnosis.isPending}
            >
              {createDiagnosis.isPending ? 'Salvando...' : 'Adicionar'}
            </Button>
          </div>
        </form>
      )}

      {!showForm && existingDiagnoses.length === 0 && (
        <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-gray-200 border-dashed">
          <p className="text-sm">Nenhum diagnóstico registrado.</p>
          <p className="text-xs mt-1">Clique em "Adicionar Diagnóstico" para começar.</p>
        </div>
      )}
    </div>
  );
}
